import { supabase } from '../lib/supabase';

export const userService = {
  // Get user profile
  async getUserProfile(userId) {
    const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();

    if (error) throw error;
    
    // Convert snake_case to camelCase for React
    return {
      id: data?.id,
      email: data?.email,
      fullName: data?.full_name,
      avatarUrl: data?.avatar_url,
      role: data?.role,
      phone: data?.phone,
      preferences: data?.preferences,
      isActive: data?.is_active,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  // Update user profile
  async updateProfile(userId, updates) {
    // Convert camelCase to snake_case for database
    const updateData = {};
    if (updates?.fullName !== undefined) updateData.full_name = updates?.fullName;
    if (updates?.avatarUrl !== undefined) updateData.avatar_url = updates?.avatarUrl;
    if (updates?.phone !== undefined) updateData.phone = updates?.phone;
    if (updates?.preferences !== undefined) updateData.preferences = updates?.preferences;

    const { data, error } = await supabase?.from('user_profiles')?.update(updateData)?.eq('id', userId)?.select()?.single();

    if (error) throw error;
    
    // Convert back to camelCase
    return {
      id: data?.id,
      email: data?.email,
      fullName: data?.full_name,
      avatarUrl: data?.avatar_url,
      role: data?.role,
      phone: data?.phone,
      preferences: data?.preferences,
      isActive: data?.is_active,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  // Check if user profile exists
  async profileExists(userId) {
    const { data, error } = await supabase?.from('user_profiles')?.select('id')?.eq('id', userId)?.single();

    if (error && error?.code !== 'PGRST116') throw error; // PGRST116 = not found
    return !!data;
  },

  // Get user preferences
  async getUserPreferences(userId) {
    const { data, error } = await supabase?.from('user_profiles')?.select('preferences')?.eq('id', userId)?.single();

    if (error) throw error;
    return data?.preferences || {};
  },

  // Update user preferences
  async updatePreferences(userId, preferences) {
    const { data, error } = await supabase?.from('user_profiles')?.update({ preferences })?.eq('id', userId)?.select('preferences')?.single();

    if (error) throw error;
    return data?.preferences || {};
  },

  // ✅ NEW: Update user preferences (convenience method for specific preference updates)
  async updateUserPreferences(userId, preferenceUpdates) {
    try {
      // Get current preferences first
      const currentPreferences = await this.getUserPreferences(userId);
      
      // Merge with updates
      const updatedPreferences = {
        ...currentPreferences,
        ...preferenceUpdates
      };
      
      // Save to database
      return await this.updatePreferences(userId, updatedPreferences);
    } catch (error) {
      console.error('Error updating user preferences:', error);
      throw error;
    }
  },

  // ✅ NEW: Get user's default trip visibility preference
  async getDefaultTripVisibility(userId) {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('default_trip_visibility')
        ?.eq('id', userId)
        ?.single();

      if (error) throw error;
      return data?.default_trip_visibility || 'private'; // Default to private
    } catch (error) {
      console.error('Error getting default trip visibility:', error);
      return 'private'; // Fail-safe to private
    }
  },

  // ✅ NEW: Update user's default trip visibility preference
  async updateDefaultTripVisibility(userId, visibility) {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.update({ default_trip_visibility: visibility })
        ?.eq('id', userId)
        ?.select('default_trip_visibility')
        ?.single();

      if (error) throw error;
      return data?.default_trip_visibility;
    } catch (error) {
      console.error('Error updating default trip visibility:', error);
      throw error;
    }
  }
};